import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SettingsPasswordProps {
  successMessage: string;
  errorMessage: string;
  isLoading: boolean;
  onSave: (currentPassword: string, newPassword: string) => void;
  onCancel: () => void;
}

/** Password reset tab — only shown for email-registered (non-federated) users */
const SettingsPassword: React.FC<SettingsPasswordProps> = ({
  successMessage,
  errorMessage,
  isLoading,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (newPassword !== confirmPassword) {
      setLocalError(t('settings.passwordMismatch', 'New passwords do not match.'));
      return;
    }

    if (newPassword.length < 12) {
      setLocalError(t('settings.passwordTooShort', 'Password must be at least 12 characters.'));
      return;
    }

    if (!/[!@#$%^&*()_+\-=[\]{}|;:',.<>?/~`]/.test(newPassword)) {
      setLocalError(
        t(
          'settings.passwordNeedsSpecialChar',
          'Password must contain at least one special character (!@#$%...).',
        ),
      );
      return;
    }

    onSave(currentPassword, newPassword);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleCancel = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setLocalError('');
    onCancel();
  };

  const displayError = localError || errorMessage;

  return (
    <div className="settings-section">
      <h2 className="settings-section-title">{t('settings.changePassword', 'Change Password')}</h2>

      {successMessage && (
        <div className="settings-message success">
          <span>{successMessage}</span>
        </div>
      )}
      {displayError && (
        <div className="settings-message error">
          <span>{displayError}</span>
        </div>
      )}

      <form className="settings-form" onSubmit={handleSubmit}>
        {/* Current Password */}
        <div className="form-group">
          <label htmlFor="currentPassword">
            {t('settings.currentPassword', 'Current Password')} *
          </label>
          <div className="password-input-wrapper">
            <input
              type={showCurrentPassword ? 'text' : 'password'}
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder={t('settings.enterPassword', 'Enter your password')}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <title>{showCurrentPassword ? 'Hide Password' : 'Show Password'}</title>
                {showCurrentPassword ? (
                  <>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </>
                ) : (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* New Password */}
        <div className="form-group">
          <label htmlFor="newPassword">{t('settings.newPassword', 'New Password')} *</label>
          <div className="password-input-wrapper">
            <input
              type={showNewPassword ? 'text' : 'password'}
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('settings.enterNewPassword', 'Enter your new password')}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <title>{showNewPassword ? 'Hide Password' : 'Show Password'}</title>
                {showNewPassword ? (
                  <>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </>
                ) : (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div className="form-group">
          <label htmlFor="confirmPassword">
            {t('settings.confirmPassword', 'Confirm New Password')} *
          </label>
          <div className="password-input-wrapper">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('settings.confirmNewPassword', 'Confirm your new password')}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <title>{showConfirmPassword ? 'Hide Password' : 'Show Password'}</title>
                {showConfirmPassword ? (
                  <>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </>
                ) : (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Password Requirements */}
        <div className="password-requirements">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <title>Warning Icon</title>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span>
            {t(
              'settings.passwordRequirements',
              'New password must contain 1 uppercase letter, 1 number, 1 special character (!@#$%...), min 8 characters',
            )}
          </span>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={handleCancel}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button type="submit" className="btn-save" disabled={isLoading}>
            {isLoading
              ? t('common.saving', 'Saving...')
              : t('settings.savePassword', 'Save new password')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SettingsPassword;
